// SPDX-License-Identifier: AGPL-3.0-or-later
import './styles.css';
import { initTooltip } from './components/tooltip';
import { initGlossary, gloss, hideGlossary } from './glossary';
import { esc, num, rate, pct, times } from './format';
import type { Dataset } from './types';
import { renderStateDrawer } from './drilldown';
import { renderOverview } from './views/overview';
import { renderGap } from './views/gap';
import { renderMap } from './views/map';
import { renderRemand } from './views/remand';
import { renderOffences } from './views/offences';
import { renderDemographics } from './views/demographics';
import { renderPrisons } from './views/prisons';
import { renderRankings } from './views/rankings';
import { renderInsights } from './views/insights';

const VIEWS = [
  { id: 'overview', label: 'Overview', render: renderOverview },
  { id: 'gap', label: 'The gap', render: renderGap },
  { id: 'map', label: 'Map', render: renderMap },
  { id: 'remand', label: 'Remand', render: renderRemand },
  { id: 'offences', label: 'Offences', render: renderOffences },
  { id: 'demographics', label: "Who's inside", render: renderDemographics },
  { id: 'prisons', label: 'Prisons', render: renderPrisons },
  { id: 'rankings', label: 'Rankings', render: renderRankings },
  { id: 'insights', label: 'Insights', render: renderInsights },
] as const;

export type ViewId = (typeof VIEWS)[number]['id'];
const STORE_KEY = 'au-imprisonment:view';

interface Route { view: ViewId; state?: string; filter?: string }

function parseHash(): Route {
  const params = new URLSearchParams(location.hash.replace(/^#/, ''));
  const view = (params.get('v') ?? '') as ViewId;
  const known = VIEWS.some((v) => v.id === view);
  const stored = localStorage.getItem(STORE_KEY) as ViewId | null;
  return {
    view: known ? view : (VIEWS.some((v) => v.id === stored) && stored ? stored : 'overview'),
    state: params.get('s') ?? undefined,
    filter: params.get('q') ?? undefined,
  };
}

export function navigate(patch: Partial<Route>, replace = false): void {
  const cur = parseHash();
  const next = { ...cur, ...patch };
  const params = new URLSearchParams();
  params.set('v', next.view);
  if (next.state) params.set('s', next.state);
  if (next.filter) params.set('q', next.filter);
  const url = `#${params.toString()}`;
  if (replace) { history.replaceState(null, '', url); render(); }
  else location.hash = url;
}

let data: Dataset | null = null;

async function load(): Promise<Dataset> {
  const bust = `?v=${Date.now().toString(36).slice(0, 6)}`;
  const names = ['meta', 'national', 'rates', 'states', 'offences', 'age', 'remand', 'prisons'];
  const [meta, national, rates, states, offences, age, remand, prisons] = await Promise.all(
    names.map(async (f) => {
      const res = await fetch(`data/${f}.json${bust}`);
      if (!res.ok) throw new Error(`Could not load ${f}.json (HTTP ${res.status})`);
      return res.json();
    }),
  );
  return { meta, national, rates, states, offences, age, remand, prisons } as Dataset;
}

function shell(): void {
  const app = document.getElementById('app')!;
  app.innerHTML = `
    <header class="site-header">
      <div class="header-inner">
        <div class="brand">
          <img class="logo" src="/favicon.svg" alt="" width="22" height="22" />
          <h1>Imprisonment</h1>
          <span class="brand-sub">Australia's prison population, by the numbers</span>
        </div>
        <div class="header-spacer"></div>
        <button class="icon-btn" id="about-btn" aria-label="About this site" title="About this site">?</button>
      </div>
      <div class="nav-wrap">
        <nav class="nav-tabs" role="tablist" aria-label="Views">
          ${VIEWS.map((v) => `<button class="nav-tab" role="tab" data-view="${v.id}" aria-selected="false">${v.label}</button>`).join('')}
        </nav>
      </div>
    </header>
    <main class="main-content" id="view-root"><div class="skeleton"></div></main>
    <footer class="site-footer">
      <div class="footer-inner">
        <p id="footer-source"></p>
        <p>Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></p>
      </div>
    </footer>
    <div class="overlay" id="overlay"></div>
    <aside class="drawer" id="drawer" role="dialog" aria-label="State detail" aria-modal="true"></aside>
    <div class="modal" id="about-modal" role="dialog" aria-label="About this site" aria-modal="true"></div>
  `;

  app.querySelectorAll<HTMLButtonElement>('.nav-tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      localStorage.setItem(STORE_KEY, btn.dataset.view!);
      navigate({ view: btn.dataset.view as ViewId, state: undefined, filter: undefined });
    });
  });

  const header = document.querySelector<HTMLElement>('.site-header')!;
  const syncHeaderHeight = () => document.documentElement.style.setProperty('--sticky-top', `${header.offsetHeight}px`);
  syncHeaderHeight();
  new ResizeObserver(syncHeaderHeight).observe(header);

  const drawerEl = document.getElementById('drawer')!;
  drawerEl.addEventListener('transitionend', (e) => {
    if (e.propertyName === 'transform' && !drawerEl.classList.contains('open')) drawerEl.style.display = 'none';
  });

  document.getElementById('about-btn')!.addEventListener('click', openAbout);
  document.getElementById('overlay')!.addEventListener('click', closeOverlays);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeOverlays(); });
}

function closeOverlays(): void {
  document.getElementById('overlay')!.classList.remove('open');
  document.getElementById('drawer')!.classList.remove('open');
  document.getElementById('about-modal')!.classList.remove('open');
  hideGlossary();
  const cur = parseHash();
  if (cur.state) navigate({ state: undefined }, true);
}

function openAbout(): void {
  const m = document.getElementById('about-modal')!;
  const meta = data?.meta;
  const c = meta?.counts;
  m.innerHTML = `
    <button class="icon-btn modal-close" aria-label="Close">✕</button>
    <h2>About this site</h2>
    <p>How many people are in prison in Australia, who they are, and the gaps a single headline rate hides. Every figure comes from the Australian Bureau of Statistics' annual <em>Prisoners in Australia</em> release — a count of everyone in adult custody on the night of 30&nbsp;June — turned into one place you can map, rank and read.</p>

    <h3>Where the data comes from</h3>
    <p>The ABS collects prisoner records from every state and territory corrective-services agency and publishes them each December. This site reads the published data cubes and joins them to official ABS state boundaries so they can be mapped. Nothing is modelled or estimated.</p>
    <ul>
      <li>ABS Prisoners in Australia ${esc(meta?.release ?? '')} — national, state, offence, age, remand and facility tables, ${esc(String(meta?.firstYear ?? ''))}–${esc(String(meta?.latestYear ?? ''))}</li>
      <li>ABS ASGS 2021 state &amp; territory boundaries</li>
    </ul>

    <h3>The headline</h3>
    <p>On 30&nbsp;June ${esc(String(meta?.latestYear ?? ''))} there were <strong>${num(c?.total)}</strong> adults in prison — an ${gloss('imprisonmentRate', 'imprisonment rate')} of <strong>${rate(c?.rate)}</strong> per 100,000. Two things a rate alone can't show: <strong>${gloss('atsi', 'Aboriginal and Torres Strait Islander')}</strong> people are imprisoned about <strong>${times(c?.ratioStd)}</strong> as often as non-Indigenous people (${gloss('ageStandardised', 'age-standardised')}), a gap that has <em>widened</em> from ${times(c?.ratioStd2016)} in ${esc(String(meta?.firstYear ?? ''))}; and <strong>${pct(c?.remandShare)}</strong> of prisoners are on <strong>${gloss('remand', 'remand')}</strong> — unsentenced and presumed innocent — up from ${pct(c?.remandShare2016)}.</p>

    <h3>How to read the numbers</h3>
    <ul>
      <li><strong>This is imprisonment, not crime.</strong> The data counts people the justice system has put in custody. It is not a measure of offending, and nowhere here is a place or group presented as a scoreboard.</li>
      <li><strong>Rates are per 100,000 relevant adults.</strong> ${gloss('crude', 'Crude rates')} partly reflect how old a population is; the ${gloss('ageStandardised', 'age-standardised')} rate removes that, and the over-representation gap barely changes.</li>
      <li><strong>Remand means unsentenced.</strong> A ${gloss('remand', 'remand')} prisoner is awaiting trial or sentencing and is legally presumed innocent.</li>
      <li><strong>One offence per prisoner.</strong> Each is counted under their ${gloss('mso', 'most serious offence/charge')} only, so the offence mix understates lesser offending.</li>
      <li><strong>Small differences are ${gloss('perturbation', 'perturbation')}.</strong> The ABS randomly adjusts small cells for privacy, so parts don't always sum exactly to totals.</li>
    </ul>
    <p>If this subject affects you or someone you know, support is available from Lifeline (13&nbsp;11&nbsp;14) and 13YARN (13&nbsp;92&nbsp;76), a 24/7 line for Aboriginal and Torres Strait Islander people.</p>

    <h3>Updates</h3>
    <p>The ABS publishes once a year, each December; this site refreshes on the same annual cadence. Source release: ${esc(meta ? new Date(meta.releaseDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : '')}.</p>
  `;
  m.querySelector('.modal-close')!.addEventListener('click', closeOverlays);
  document.getElementById('overlay')!.classList.add('open');
  m.classList.add('open');
  (m.querySelector('.modal-close') as HTMLElement).focus();
}

export function openState(code: string): void { navigate({ state: code }); }

function render(): void {
  if (!data) return;
  const route = parseHash();
  document.querySelectorAll<HTMLButtonElement>('.nav-tab').forEach((b) => {
    b.setAttribute('aria-selected', String(b.dataset.view === route.view));
  });

  const root = document.getElementById('view-root')!;
  const view = VIEWS.find((v) => v.id === route.view) ?? VIEWS[0];
  root.scrollTop = 0;
  try {
    (view.render as (r: HTMLElement, d: Dataset, f?: string) => void)(root, data, route.filter);
  } catch (err) {
    root.innerHTML = `<div class="error-state"><p>Something went wrong drawing this view.</p><p>${esc(err instanceof Error ? err.message : String(err))}</p></div>`;
  }

  const drawer = document.getElementById('drawer')!;
  const overlay = document.getElementById('overlay')!;
  const aboutOpen = document.getElementById('about-modal')!.classList.contains('open');
  let opened = false;
  if (route.state) {
    const st = data.states.find((s) => s.code === route.state);
    if (st) { renderStateDrawer(drawer, st, data); opened = true; }
  }
  if (opened) {
    drawer.querySelector('.drawer-close')?.addEventListener('click', closeOverlays);
    overlay.classList.add('open');
    drawer.style.display = 'block';
    void drawer.offsetWidth;
    drawer.classList.add('open');
    (drawer.querySelector('.drawer-close') as HTMLElement | null)?.focus();
  } else if (!aboutOpen) {
    drawer.classList.remove('open');
    overlay.classList.remove('open');
  }
}

async function boot(): Promise<void> {
  shell();
  initTooltip();
  initGlossary();
  window.addEventListener('hashchange', render);

  try {
    data = await load();
  } catch (err) {
    document.getElementById('view-root')!.innerHTML = `
      <div class="error-state">
        <p>Could not load the imprisonment data.</p>
        <p>${esc(err instanceof Error ? err.message : String(err))}</p>
        <p><button class="icon-btn" style="width:auto;padding:0 12px;border-radius:8px" onclick="location.reload()">Retry</button></p>
      </div>`;
    return;
  }

  const c = data.meta.counts;
  document.getElementById('footer-source')!.innerHTML =
    `Source: Australian Bureau of Statistics, Prisoners in Australia ${esc(data.meta.release)} (CC BY 4.0) and ABS ASGS 2021 boundaries. ` +
    `${esc(num(c.total))} prisoners on 30 June ${esc(String(data.meta.latestYear))}; rates per 100,000 adults. ` +
    `This site measures imprisonment, not offending.`;

  render();
}

void boot();
